import { checkoutPlatform } from '../lib/hostedCheckout'
import { useMemberBookingRecovery } from '../lib/useMemberBookingRecovery'
import { formatFacilityBookingDate, formatFacilityMinutes } from '../utils/facilityBookingPresentation'
import StableFacilityImage from '../components/StableFacilityImage'
import { useOptionalPrivanaI18n } from '@/i18n/PrivanaI18nProvider'
import MemberFacilityCheckout from '../components/MemberFacilityCheckout'
import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { Shield, Users } from 'lucide-react'
import { useMemberPortalApi } from '../api/memberPortalApiClient'
import BackPillButton from '../components/BackPillButton'
import MemberPaymentMethodOptions from '../components/MemberPaymentMethodOptions'
import { createPayNowHandoff, openPayNowCheckoutPlaceholder, subscribePayNowCompleted, updatePayNowHandoff } from '../utils/payNowTabHandoff'
import { commitMemberBookingSuccess, createCommittedBooking } from '../lib/upcomingBookingsCache'
import {
  DEFAULT_GOLF_TIME_ZONE,
  normalizeDateKey,
} from '../utils/golfTime'

const CURRENCY_SYMBOLS = { GBP: '£', USD: '$', EUR: '€' }

function normalizeSlot(value) {
  if (!value) return null
  const trimmed = String(value).trim()
  return trimmed || null
}

function formatPrice(value, currency, locale) {
  if (value == null || value === '') return null
  const numeric = Number(value)
  if (!Number.isFinite(numeric)) return null
  const code = currency || ''
  const symbol = CURRENCY_SYMBOLS[code] || ''
  const formatted = numeric.toLocaleString(locale, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
  if (symbol) return `${symbol}${formatted}`
  if (code) return `${formatted} ${code}`
  return formatted
}

function resolveBookingErrorMessage(errorCode, t) {
  const keys = { slot_unavailable: 'unavailable', slot_in_past: 'unavailable', already_booked_for_occurrence: 'alreadyBooked', popup_blocked: 'popup', spa_accounting_sync_failed: 'accounting' }
  return t(`facilityBooking.${keys[errorCode] || 'failed'}`)
}

export default function MemberSpaBookingConfirmPage({ clubCode, member }) {
  const { t, locale } = useOptionalPrivanaI18n()
  const { serviceId } = useParams()
  const memberName = member?.full_name || [member?.first_name || member?.firstName, member?.last_name || member?.lastName].filter(Boolean).join(' ') || t('facilityBooking.member')
  const navigate = useNavigate()
  const location = useLocation()
  const [searchParams] = useSearchParams()
  const { memberPortalApiGet, memberPortalApiPost } = useMemberPortalApi()



  const selectedDateKey = useMemo(() => normalizeDateKey(searchParams.get('date')), [searchParams])
  const slotStartAt = useMemo(() => normalizeSlot(searchParams.get('start_at')), [searchParams])
  const slotStartTime = useMemo(() => normalizeSlot(searchParams.get('start_time')), [searchParams])
  const durationParam = searchParams.get('duration')

  const [service, setService] = useState(location.state?.service || null)
  const [serviceLoading, setServiceLoading] = useState(!location.state?.service)
  const [serviceError, setServiceError] = useState(null)
  const [availabilityLoading, setAvailabilityLoading] = useState(false)
  const [availabilityError, setAvailabilityError] = useState(null)
  const [timeZone, setTimeZone] = useState(DEFAULT_GOLF_TIME_ZONE)
  const [selectedSlot, setSelectedSlot] = useState(location.state?.slot || null)
  const [durationMinutes, setDurationMinutes] = useState(durationParam || location.state?.durationMinutes || null)
  const [pricing, setPricing] = useState(null)
  const [saving, setSaving] = useState(false)
  const [paymentMethod, setPaymentMethod] = useState('member_account')
  const [submitError, setSubmitError] = useState(null)
  const recovery = useMemberBookingRecovery({ clubCode, module: 'spa', memberId: member?.id || member?.member_id, post: memberPortalApiPost,
    onCompleted: bookingId => navigate(`/${clubCode}/spa/booking/success?booking_id=${bookingId}`, { replace: true }),
    onReleased: () => { submissionLock.current = false; setSubmitError(null) },
  })
  const outcomePending = recovery.pending
  useEffect(() => subscribePayNowCompleted({ module: 'spa', clubCode, onCompleted: () => recovery.reconcile() }), [clubCode, recovery.reconcile])
  const flowActive = useRef(true)
  useEffect(() => {
    flowActive.current = true
    return () => { flowActive.current = false }
  }, [])
  const submissionLock = useRef(false)
  const errorRef = useRef(null)
  useEffect(() => {
    if (submitError) { errorRef.current?.scrollIntoView?.({ block: 'center', behavior: 'instant' }); errorRef.current?.focus({ preventScroll: true }) }
  }, [submitError])

  useEffect(() => {
    if (selectedDateKey && (slotStartAt || slotStartTime)) return
    navigate(`/${clubCode}/spa/${serviceId}${selectedDateKey ? `?date=${selectedDateKey}` : ''}`, { replace: true })
  }, [clubCode, navigate, selectedDateKey, serviceId, slotStartAt, slotStartTime])

  useEffect(() => {
    let cancelled = false
    async function loadService() {
      if (!clubCode || !serviceId || service) return
      setServiceLoading(true)
      setServiceError(null)
      try {
        const res = await memberPortalApiGet(`/api/members-portal/${clubCode}/spa/services/${serviceId}`)
        const json = await res.json().catch(() => null)
        if (!res.ok) throw new Error(json?.error || 'unable_to_load_service')
        if (!cancelled) setService(json?.service || null)
      } catch (err) {
        if (!cancelled) setServiceError('facilityBooking.serviceFailed')
      } finally {
        if (!cancelled) setServiceLoading(false)
      }
    }
    loadService()
    return () => { cancelled = true }
  }, [clubCode, memberPortalApiGet, service, serviceId])

  useEffect(() => {
    let cancelled = false
    async function loadAvailability() {
      if (!clubCode || !serviceId || !selectedDateKey) return
      setAvailabilityLoading(true)
      setAvailabilityError(null)
      try {
        const next = new URLSearchParams()
        next.set('date', selectedDateKey)
        if (slotStartAt) next.set('start_at', slotStartAt)
        if (slotStartTime) next.set('start_time', slotStartTime)
        const res = await memberPortalApiGet(`/api/members-portal/${clubCode}/spa/services/${serviceId}/booking-preview?${next.toString()}`)
        const json = await res.json().catch(() => null)
        if (res.status === 409) throw new Error(json?.error || 'slot_unavailable')
        if (!res.ok) throw new Error(json?.error || 'availability_unavailable')
        if (cancelled) return
        setService(json?.service || service || null)
        setSelectedSlot(json?.slot || null)
        setTimeZone(json?.time_zone || DEFAULT_GOLF_TIME_ZONE)
        setDurationMinutes(json?.duration_minutes ?? durationParam ?? null)
        setPricing(json?.pricing || null)
        if (!json?.slot) setAvailabilityError('facilityBooking.unavailable')
      } catch (err) {
        if (!cancelled) {
          const message = err?.message === 'slot_unavailable' || err?.message === 'slot_in_past'
            ? 'facilityBooking.unavailable'
            : 'facilityBooking.previewFailed'
          setAvailabilityError(message)
          setSelectedSlot(null)
          setPricing(null)
        }
      } finally {
        if (!cancelled) setAvailabilityLoading(false)
      }
    }
    loadAvailability()
    return () => { cancelled = true }
  }, [clubCode, durationParam, memberPortalApiGet, selectedDateKey, serviceId, slotStartAt, slotStartTime])

  const serviceName = service?.name || t('facilityBooking.spaServiceLabel')
  const dateLabel = selectedDateKey ? formatFacilityBookingDate(selectedDateKey, locale) : null
  const timeLabel = selectedSlot?.start_time || slotStartTime || t('facilityBooking.selectedTime')
  const durationLabel = formatFacilityMinutes(durationMinutes || service?.duration_minutes, locale) || t('facilityBooking.durationUnavailable')
  const totalLabel = formatPrice(pricing?.final_price, pricing?.currency, locale) || t('facilityBooking.tbd')
  const serviceImage = service?.image_url || service?.photo_url || '/images/luxury_club_image.png'

  const handleSubmit = async (event) => {
    event.preventDefault()
    if (submissionLock.current || outcomePending) return
    if (!selectedDateKey || !selectedSlot?.start_time || !selectedSlot?.start_at) {
      setSubmitError('facilityBooking.invalid')
      return
    }
    submissionLock.current = true
    setSaving(true)
    setSubmitError(null)
    let requestSent = false
    let responseReceived = false
    let checkoutOpened = false
    let outcomeUncertain = false
    let checkoutWindow = null
    try {
      const bookingPayload = {
        service_id: serviceId,
        date: selectedDateKey,
        start_time: selectedSlot.start_time,
        start_at: selectedSlot.start_at || slotStartAt,
        request_key: recovery.requestKey(),
        payment_method: paymentMethod === 'pay_now' && Number(pricing?.final_price) > 0 ? 'pay_now' : 'account',
        estimated_amount: Number(pricing?.final_price ?? 0),
      }
      if (paymentMethod === 'pay_now' && Number(pricing?.final_price) > 0) {
        const handoff = createPayNowHandoff({ module: 'spa', clubCode, successPath: `/${clubCode}/spa/booking/success` })
        checkoutWindow = openPayNowCheckoutPlaceholder(handoff, { locale })
        recovery.markPending()
        requestSent = true
        const checkoutRes = await memberPortalApiPost(`/api/members-portal/${clubCode}/booking-payments/checkout`, { platform: checkoutPlatform(), module: 'spa', booking_payload: bookingPayload })
        responseReceived = true
        const checkoutJson = await checkoutRes.json().catch(() => null)
        if (!flowActive.current) { checkoutWindow?.close?.(); return }
        if(checkoutRes.ok && checkoutJson?.booking_id && checkoutJson?.status==='completed'){checkoutWindow.close();recovery.release();navigate(`/${clubCode}/spa/booking/success?booking_id=${checkoutJson.booking_id}`);return}
        if (checkoutRes.status >= 500) throw new Error('booking_outcome_unknown')
        if (checkoutRes.ok && !checkoutJson?.url) throw new Error('booking_outcome_unknown')
        if (!checkoutRes.ok || !checkoutJson?.url) throw new Error(checkoutJson?.error || 'checkout_failed')
        updatePayNowHandoff(handoff.key, { checkout_session_id: checkoutJson.checkout_session_id || checkoutJson.session_id || checkoutJson.sessionId || null, payment_attempt_id: checkoutJson.payment_attempt_id || checkoutJson.paymentAttemptId || null })
        recovery.watchCheckout(checkoutWindow)
        await checkoutWindow.location.assign(checkoutJson.url)
        checkoutOpened = true
        recovery.markPending()
        return
      }
      recovery.markPending()
      requestSent = true
      const res = await memberPortalApiPost(`/api/members-portal/${clubCode}/spa/bookings`, bookingPayload)
      responseReceived = true
      const json = await res.json().catch(() => null)
      if (!flowActive.current) {
        window.dispatchEvent(new CustomEvent('members:upcoming-bookings-invalidated'))
        return
      }
      if (res.status >= 500) throw new Error('booking_outcome_unknown')
      if (!res.ok) throw new Error(typeof json?.error === 'string' ? json.error : 'booking_failed')
      const bookingId = json?.booking_id
      if (!bookingId) throw new Error('booking_outcome_unknown')
      recovery.release()
      const booking = createCommittedBooking({ id: bookingId, module: 'spa', title: serviceName, location: selectedSlot?.room_name || service?.staff_name,
        startAt: selectedSlot.start_at || slotStartAt, timeZone, imageUrl: serviceImage, amount: Number(json?.price_total ?? pricing?.final_price), currency: json?.currency || pricing?.currency,
        details: { duration_minutes: durationMinutes || service?.duration_minutes, participants: [{ name: memberName }], payment_method: 'member_account', payment_summary: 'Charged to member account' } })
      commitMemberBookingSuccess(booking, { clubCode })
      navigate(`/${clubCode}/spa/booking/success${bookingId ? `?booking_id=${bookingId}` : ''}`, { state: { booking } })
    } catch (err) {
      checkoutWindow?.close?.()
      if (!flowActive.current) return
      if ((requestSent && !responseReceived) || err?.message === 'booking_outcome_unknown') {
        outcomeUncertain = true
        recovery.markPending()
        setSubmitError('facilityBooking.unknown')
      } else { recovery.release(); setSubmitError(resolveBookingErrorMessage(err?.message, key => key)) }
    } finally {
      if (!checkoutOpened && !outcomeUncertain) submissionLock.current = false
      if (flowActive.current) setSaving(false)
    }
  }

  return (
    <div className="mp-tee-booking mp-tee-booking--facility">
      <div className="mp-tee-booking__header">
        <BackPillButton className="mp-tee-booking__back" to={`/${clubCode}/spa/${serviceId}${selectedDateKey ? `?date=${selectedDateKey}` : ''}`} state={service ? { service } : undefined}>
          {t('facilityBooking.backTimes')}
        </BackPillButton>
        <div className="mp-tee-booking__title"><h3><strong>{serviceName}</strong>{dateLabel ? ` – ${dateLabel}` : ''}{timeLabel ? ` – ${timeLabel}` : ''}</h3></div>
        <div aria-hidden className="mp-tee-booking__spacer" />
      </div>

      {(serviceError || availabilityError || submitError) && <div className="mp-warning" role="alert" data-mp-static-focus="" tabIndex={-1} ref={errorRef}>{t(submitError || availabilityError || serviceError)}</div>}

      <form className="mp-tee-booking__grid" onSubmit={handleSubmit}>
        <div className="mp-tee-booking__media">
          <div className="mp-tee-booking__image mp-facility-review-image"><StableFacilityImage key={`${clubCode}:${serviceId}`} identity={`${clubCode}:${serviceId}`} url={serviceImage} /></div>
          <div className="mp-tee-booking__compact-summary"><strong>{serviceName}</strong><span>{dateLabel} · {timeLabel} · {durationLabel}</span>{selectedSlot?.room_name || service?.staff_name ? <span>{selectedSlot?.room_name || service?.staff_name}</span> : null}</div>
        </div>
        <aside className="mp-tee-booking__summary">
          <div className="mp-summary-card">
            <div className="mp-summary-header"><h4>{t('facilityBooking.review')}</h4></div>
            <div className="mp-summary-section">
              <div className="mp-summary-row"><Users size={16} /><div><p className="mp-label">{t('facilityBooking.attendee')}</p><p className="mp-value">{memberName}</p></div></div>
              <div className="mp-summary-row"><Shield size={16} /><div><p className="mp-label">{t('facilityBooking.payer')}</p><p className="mp-value">{memberName}</p></div></div>
            </div>
            {Number(pricing?.final_price) > 0 ? <MemberPaymentMethodOptions value={paymentMethod} onChange={setPaymentMethod} disabled={saving} mobileCards /> : pricing?.final_price != null ? <p>{t('facilityBooking.noPayment')}</p> : null}

          </div>
        </aside>
        <MemberFacilityCheckout recovery={saving ? null : recovery} total={totalLabel} disabled={outcomePending || serviceLoading || availabilityLoading || !pricing || !selectedSlot || Boolean(availabilityError)} saving={saving} label={t('facilityBooking.confirm')} savingLabel={t('facilityBooking.confirming')} reason={outcomePending ? null : serviceLoading || availabilityLoading ? t('facilityBooking.checking') : !selectedSlot ? t('facilityBooking.chooseTime') : null} />
      </form>
    </div>
  )
}
